import { useEffect, useState } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import { signIn, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';

const Login = ({ setUser, setShowAuth, initialAuthMode = 'signin' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showUnconfirmedModal, setShowUnconfirmedModal] = useState(false);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('info');
  // consentGiven value is not read directly; the setter is wired to the form checkbox
  // and submit-side logic gates on DOM state. Keep the setter only.
  const [, setConsentGiven] = useState(false);

  // Function to show alert messages
  const showAlertMessage = (message, type = 'info') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 6000);
  };

  // Function to clear user-specific data from localStorage
  const clearUserData = () => {
    localStorage.removeItem('cv_latest_feedback');
    localStorage.removeItem('cv_analysis_errors');
    localStorage.removeItem('cv_analysis_scores');
    console.log('Cleared user-specific data from localStorage');
  };

  // Function to handle unconfirmed user during sign-in
  const handleUnconfirmedUser = async (email) => {
    try {
      setIsLoading(true);
      setLoadingMessage('Resending verification code...');
      
      // Clear any existing error messages first
      setShowAlert(false);
      setAlertMessage('');
      
      // Resend the confirmation code
      await resendSignUpCode({ username: email });
      
      setUnconfirmedEmail(email);
      setShowUnconfirmedModal(true);
      setIsLoading(false);
      
      showAlertMessage('Verification code sent to your email. Please check your inbox and complete verification.', 'info');
    } catch (error) {
      console.error('Error resending confirmation code:', error);
      setIsLoading(false);
      
      if (error.name === 'UserNotFoundException') {
        showAlertMessage('Account not found. Please sign up first.', 'error');
      } else {
        showAlertMessage('Failed to resend verification code. Please try again.', 'error');
      }
    }
  };

  // Function to handle email verification
  const handleEmailVerification = async (code) => {
    try {
      setIsLoading(true);
      setLoadingMessage('Verifying your email...');
      
      // Clear any existing error messages first
      setShowAlert(false);
      setAlertMessage('');
      
      await confirmSignUp({
        username: unconfirmedEmail,
        confirmationCode: code
      });
      
      setShowUnconfirmedModal(false);
      setIsLoading(false);
      
      showAlertMessage('Email verified successfully! You can now sign in.', 'success');
      
      // Auto-trigger sign in after successful verification
      setTimeout(() => {
        window.location.reload(); // Refresh to reset the authenticator state
      }, 2000);
      
    } catch (error) {
      console.error('Error confirming sign up:', error);
      setIsLoading(false);
      
      if (error.name === 'CodeMismatchException') {
        showAlertMessage('Invalid verification code. Please try again.', 'error');
      } else if (error.name === 'ExpiredCodeException') {
        showAlertMessage('Verification code expired. Requesting a new one...', 'error');
        // Automatically resend code
        setTimeout(() => handleUnconfirmedUser(unconfirmedEmail), 1000);
      } else {
        showAlertMessage('Verification failed. Please try again.', 'error');
      }
    }
  };

  // Add state to track which form is being displayed
  const [currentFormType, setCurrentFormType] = useState(initialAuthMode);

  // Enhanced form submission handling with consent checkbox injection
  useEffect(() => {
    // Function to inject consent checkbox into signup form
    const injectConsentCheckbox = () => {
      const signupForm = document.querySelector('[data-amplify-authenticator-signup]');
      if (signupForm && !document.querySelector('.consent-container')) {
        const submitButton = signupForm.querySelector('button[type="submit"]');
        if (submitButton) {
          const consentDiv = document.createElement('div');
          consentDiv.className = 'consent-container';
          consentDiv.innerHTML = `
            <div class="consent-checkbox">
              <input type="checkbox" id="gdpr-consent" required />
              <label for="gdpr-consent">
                I have read and agree to the 
                <a href="/terms" target="_blank">Terms of Service</a> and 
                <a href="/privacy" target="_blank">Privacy Policy</a>. 
                I understand that my CV will be processed by AI (OpenAI GPT-4o) 
                and consent to the processing of my personal data as described.
              </label>
            </div>
          `;
          
          submitButton.parentNode.insertBefore(consentDiv, submitButton);
          
          // Add event listener to checkbox
          const checkbox = document.getElementById('gdpr-consent');
          checkbox.addEventListener('change', (e) => {
            setConsentGiven(e.target.checked);
            submitButton.disabled = !e.target.checked;
            if (!e.target.checked) {
              submitButton.style.opacity = '0.5';
              submitButton.style.cursor = 'not-allowed';
            } else {
              submitButton.style.opacity = '1';
              submitButton.style.cursor = 'pointer';
            }
          });
          
          // Initially disable submit button
          submitButton.disabled = true;
          submitButton.style.opacity = '0.5';
          submitButton.style.cursor = 'not-allowed';
        }
      }
    };

    // Function to detect form type based on visible fields
    const detectFormType = () => {
      const firstNameField = document.querySelector('input[name="given_name"]');
      const lastNameField = document.querySelector('input[name="family_name"]');
      
      // If we find name fields, it's the signup form
      if (firstNameField || lastNameField) {
        setCurrentFormType('signup');
        // Inject consent checkbox for signup form
        setTimeout(injectConsentCheckbox, 100);
      } else {
        setCurrentFormType('signin');
      }
    };

    // Check form type on mount and when DOM changes
    detectFormType();
    
    // Create observer to detect form changes
    const observer = new MutationObserver(() => {
      detectFormType();
      injectConsentCheckbox();
    });
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true 
    });

    const handleSubmit = async (e) => {
      const form = e.target.closest('form');
      if (!form) return;

      const emailField = form.querySelector('input[name="username"]');
      const passwordField = form.querySelector('input[type="password"]');
      const firstNameField = form.querySelector('input[name="given_name"]');
      const confirmCodeField = form.querySelector('input[name="confirmation_code"]');
      
      // Handle email verification
      if (confirmCodeField && confirmCodeField.value) {
        e.preventDefault();
        await handleEmailVerification(confirmCodeField.value);
        return;
      }

      // Handle sign up
      if (firstNameField && emailField && passwordField && 
          firstNameField.value && emailField.value && passwordField.value) {
        setIsLoading(true);
        setLoadingMessage('Creating your account...');
        // Clear any existing user data when creating new account
        clearUserData();
        return;
      }

      // Handle sign in - check for unconfirmed users
      if (emailField && passwordField && 
          emailField.value && passwordField.value && !firstNameField) {
        
        try {
          setIsLoading(true);
          setLoadingMessage('Signing you in...');
          
          // Clear any existing user data before signing in
          clearUserData();
          
          // Clear any existing error messages first
          setShowAlert(false);
          setAlertMessage('');
          
          // Attempt to sign in first
          await signIn({
            username: emailField.value,
            password: passwordField.value
          });
          
          // If sign in is successful, the user will be set by the Authenticator
          
        } catch (error) {
          console.error('Sign in error:', error);
          setIsLoading(false);
          
          if (error.name === 'UserNotConfirmedException') {
            // User exists but email is not confirmed
            e.preventDefault();
            setTimeout(() => handleUnconfirmedUser(emailField.value), 100);
          } else if (error.name === 'NotAuthorizedException') {
            showAlertMessage('Invalid email or password. Please try again.', 'error');
          } else if (error.name === 'UserNotFoundException') {
            // Enhanced error message for deleted/non-existent users
            showAlertMessage('Account not found. Would you like to create a new account?', 'error');
            setTimeout(() => {
              if (window.confirm('Account not found. Would you like to create a new account?')) {
                // Switch to sign up mode
                setShowAuth(false);
                setTimeout(() => setShowAuth(true), 100);
              }
            }, 2000);
          } else {
            showAlertMessage('Sign in failed. Please try again.', 'error');
          }
        }
      }
    };

    // Add event listener for form submissions
    document.addEventListener('submit', handleSubmit);

    return () => {
      document.removeEventListener('submit', handleSubmit);
      observer.disconnect();
    };
    // run once on mount; handlers close over their setters which are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add CSS styles including consent checkbox styling
  useEffect(() => {
    const style = document.createElement('style');
    
    style.textContent = `
      /* Hide Amplify tabs */
      .amplify-tabs__list {
        display: none !important;
      }
      
      /* Transparent backgrounds */
      .amplify-authenticator,
      .amplify-authenticator *,
      .amplify-authenticator *::before,
      .amplify-authenticator *::after {
        --amplify-colors-background-primary: transparent !important;
        --amplify-colors-background-secondary: transparent !important;
        --amplify-colors-background-tertiary: transparent !important;
        --amplify-colors-border-primary: transparent !important;
        --amplify-colors-border-secondary: transparent !important;
        --amplify-colors-border-tertiary: transparent !important;
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
      }
      
      .amplify-authenticator [class*="amplify-"],
      .amplify-authenticator form,
      .amplify-authenticator form > div,
      .amplify-authenticator .amplify-flex,
      .amplify-authenticator .amplify-field,
      .amplify-authenticator .amplify-field-group {
        padding-left: 0 !important;
        padding-right: 0 !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
      
      .amplify-authenticator {
        --amplify-colors-brand-primary: #667eea;
        --amplify-colors-brand-primary-hover: #764ba2;
        --amplify-radii-small: 8px;
        --amplify-radii-medium: 12px;
      }
      
      .amplify-authenticator > div,
      .amplify-authenticator > div > div,
      .amplify-authenticator > div > div > div,
      .amplify-tabs,
      .amplify-tabs__panel,
      .amplify-card,
      .amplify-flex,
      [data-amplify-authenticator],
      [data-amplify-container],
      [data-amplify-router],
      [data-amplify-slot],
      .amplify-authenticator form,
      .amplify-authenticator form > div,
      .amplify-authenticator form > .amplify-flex,
      .amplify-authenticator [class*="amplify-"] {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      
      .amplify-authenticator form > .amplify-flex {
        padding-top: 1rem !important;
        padding-bottom: 1rem !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        margin: 0 !important;
        width: 100% !important;
      }
      
      .amplify-authenticator [data-amplify-authenticator-signin] .amplify-field,
      .amplify-authenticator [data-amplify-authenticator-signup] .amplify-field {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
      }
      
      .amplify-input,
      input[type="email"],
      input[type="text"]:not(.amplify-field-group input),
      input[name="given_name"],
      input[name="family_name"],
      input[name="username"],
      input[name="confirmation_code"] {
        border-radius: 12px !important;
        border: 3px solid #e0e0e0 !important;
        padding: 0.75rem 1rem !important;
        font-size: 1rem !important;
        transition: all 0.2s !important;
        height: 52px !important;
        box-sizing: border-box !important;
        background-color: white !important;
        width: 100% !important;
        margin: 0 !important;
      }
      
      .amplify-input:focus,
      input[type="email"]:focus,
      input[type="text"]:focus:not(.amplify-field-group input),
      input[name="given_name"]:focus,
      input[name="family_name"]:focus,
      input[name="username"]:focus,
      input[name="confirmation_code"]:focus {
        border-color: #667eea !important;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
        outline: none !important;
        transform: none !important;
      }
      
      .amplify-field-group {
        display: flex !important;
        gap: 0 !important;
        background: transparent !important;
        border: none !important;
        position: relative !important;
        width: 100% !important;
        margin: 0 !important;
      }
      
      .amplify-field-group__field-wrapper {
        flex: 1 !important;
        background: transparent !important;
        border: none !important;
        margin: 0 !important;
      }
      
      .amplify-field-group input[type="password"],
      .amplify-field-group input[type="text"] {
        border-radius: 12px 0 0 12px !important;
        border: 3px solid #e0e0e0 !important;
        border-right: 3px solid #e0e0e0 !important;
        border-top-right-radius: 4px !important;
        border-bottom-right-radius: 4px !important;
        padding: 0.75rem 1rem !important;
        height: 52px !important;
        box-sizing: border-box !important;
        font-size: 1rem !important;
        transition: all 0.2s !important;
        background-color: white !important;
        margin: 0 !important;
        width: 100% !important;
      }
      
      .amplify-field-group input[type="password"]:focus,
      .amplify-field-group input[type="text"]:focus {
        border-color: #667eea !important;
        border-right-color: #667eea !important;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
        outline: none !important;
      }
      
      .amplify-field__show-password {
        position: relative !important;
        border: 3px solid #e0e0e0 !important;
        border-left: 3px solid #e0e0e0 !important;
        border-top-left-radius: 4px !important;
        border-bottom-left-radius: 4px !important;
        border-top-right-radius: 12px !important;
        border-bottom-right-radius: 12px !important;
        padding: 0.75rem 1rem !important;
        background-color: white !important;
        height: 52px !important;
        box-sizing: border-box !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin-left: -3px !important;
        margin-right: 0 !important;
      }
      
      .amplify-field-group input[type="password"]:focus + .amplify-field__show-password,
      .amplify-field-group input[type="text"]:focus + .amplify-field__show-password {
        border-color: #667eea !important;
        border-left-color: #667eea !important;
      }
      
      .amplify-field {
        position: relative !important;
        background: transparent !important;
        border: none !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
      }
      
      .amplify-button--primary {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        border: none !important;
        border-radius: 25px !important;
        padding: 0.75rem 2rem !important;
        font-size: 1rem !important;
        font-weight: 600 !important;
        transition: all 0.3s !important;
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3) !important;
      }
      
      .amplify-button--primary:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4) !important;
      }
      
      .amplify-label {
        font-weight: 600 !important;
        color: #333 !important;
        margin-bottom: 0.5rem !important;
        background: transparent !important;
      }
      
      .amplify-flex {
        gap: 1rem !important;
        background: transparent !important;
        border: none !important;
      }
      
      .amplify-button--link {
        color: #667eea !important;
        font-weight: 500 !important;
        transition: all 0.2s !important;
        background: transparent !important;
      }
      
      .amplify-button--link:hover {
        color: #764ba2 !important;
        text-decoration: none !important;
      }
      
      .amplify-alert {
        border-radius: 8px !important;
        padding: 1rem !important;
        background-color: rgba(254, 215, 215, 0.9) !important;
        border: 1px solid #f56565 !important;
      }

      /* Consent checkbox styling */
      .consent-container {
        margin: 1rem 0;
        padding: 1rem;
        background: rgba(102, 126, 234, 0.05);
        border: 1px solid rgba(102, 126, 234, 0.2);
        border-radius: 8px;
      }
      
      .consent-checkbox {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
      }
      
      .consent-checkbox input[type="checkbox"] {
        margin-top: 0.25rem;
        width: 18px !important;
        height: 18px !important;
        cursor: pointer;
      }
      
      .consent-checkbox label {
        color: #333;
        font-size: 0.9rem;
        line-height: 1.4;
        cursor: pointer;
      }
      
      .consent-checkbox a {
        color: #667eea;
        text-decoration: underline;
      }
      
      .consent-checkbox a:hover {
        color: #764ba2;
      }

      /* Modal styles */
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        backdrop-filter: blur(5px);
      }
      
      .modal-content {
        background: white;
        border-radius: 16px;
        padding: 2rem;
        max-width: 400px;
        width: 90%;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        position: relative;
      }
      
      .modal-input {
        width: 100%;
        padding: 0.75rem 1rem;
        border: 3px solid #e0e0e0;
        border-radius: 12px;
        font-size: 1rem;
        transition: all 0.2s;
        box-sizing: border-box;
      }
      
      .modal-input:focus {
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        outline: none;
      }
      
      .modal-button {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border: none;
        border-radius: 25px;
        padding: 0.75rem 2rem;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s;
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
      }
      
      .modal-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
      }
      
      .modal-button-secondary {
        background: transparent;
        color: #667eea;
        border: 2px solid #667eea;
        border-radius: 25px;
        padding: 0.75rem 2rem;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s;
      }
      
      .modal-button-secondary:hover {
        background: #667eea;
        color: white;
      }

      @keyframes float {
        0% { transform: translateY(0px); }
        50% { transform: translateY(-30px); }
        100% { transform: translateY(0px); }
      }
      
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Email verification modal component
  const EmailVerificationModal = () => {
    const [verificationCode, setVerificationCode] = useState('');
    
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h2 style={{ 
            marginBottom: '1rem', 
            color: '#333', 
            textAlign: 'center',
            fontSize: '1.5rem',
            fontWeight: 'bold'
          }}>
            Verify Your Email
          </h2>
          
          <p style={{ 
            marginBottom: '1rem', 
            color: '#666', 
            textAlign: 'center',
            lineHeight: '1.5'
          }}>
            Please enter the verification code sent to:<br />
            <strong style={{ color: '#667eea' }}>{unconfirmedEmail}</strong>
          </p>
          
          {/* Information about verification */}
          <div style={{ 
            backgroundColor: '#FEF3C7', 
            border: '2px solid #F59E0B',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '0.5rem' 
            }}>
              <strong style={{ color: '#92400E', fontSize: '0.95rem' }}>
                Important: Complete verification now
              </strong>
            </div>
            <p style={{ 
              color: '#92400E', 
              fontSize: '0.85rem',
              margin: 0,
              lineHeight: '1.4'
            }}>
              Your account needs email verification to be activated. If you close this window or navigate away, you'll need to contact support or try the "Resend Code" option to get back to this step.
            </p>
          </div>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <input
              type="text"
              className="modal-input"
              placeholder="Enter verification code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              maxLength="6"
              style={{ 
                textAlign: 'center', 
                fontSize: '1.2rem', 
                letterSpacing: '0.2em'
              }}
            />
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              className="modal-button"
              onClick={() => handleEmailVerification(verificationCode)}
              disabled={!verificationCode || verificationCode.length < 6}
              style={{ 
                opacity: (!verificationCode || verificationCode.length < 6) ? 0.5 : 1,
                cursor: (!verificationCode || verificationCode.length < 6) ? 'not-allowed' : 'pointer'
              }}
            >
              Verify Email
            </button>
            
            <button
              className="modal-button-secondary"
              onClick={() => handleUnconfirmedUser(unconfirmedEmail)}
            >
              Resend Code
            </button>
          </div>
          
          {/* Help text */}
          <div style={{ 
            marginTop: '1.5rem', 
            padding: '0.75rem',
            backgroundColor: '#F0F9FF',
            borderRadius: '6px',
            border: '1px solid #E0E7FF'
          }}>
            <p style={{ 
              color: '#3730A3', 
              fontSize: '0.8rem',
              margin: 0,
              textAlign: 'center',
              lineHeight: '1.3'
            }}>
              <strong>Tip:</strong> Check your spam folder if you don't see the email. 
              The verification code is usually delivered within a few minutes.
            </p>
          </div>
          
          <button
            onClick={() => setShowUnconfirmedModal(false)}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#999',
              padding: '0.5rem'
            }}
          >
            ×
          </button>
        </div>
      </div>
    );
  };

  const components = {
    Header() {
      return null;
    },
  };

  return (
    <div style={{ 
      position: 'relative',
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      overflow: 'hidden'
    }}>
      {/* Background and layout */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          zIndex: -1
        }}
      >
        {/* Animated bubbles */}
        <div style={{ position: 'absolute', top: '-100px', left: '-100px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', animation: 'float 6s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '-150px', right: '-150px', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', animation: 'float 8s ease-in-out infinite reverse' }} />
        <div style={{ position: 'absolute', top: '40%', left: '10%', width: '250px', height: '250px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', animation: 'float 10s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '30%', right: '20%', width: '350px', height: '350px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', animation: 'float 12s ease-in-out infinite reverse' }} />
        <div style={{ position: 'absolute', top: '60%', right: '40%', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.09)', animation: 'float 15s ease-in-out infinite' }} />
      </div>

      {/* Header */}
      <div style={{ backgroundColor: '#2e3a46', padding: '1.5rem 2rem', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', position: 'relative', zIndex: 1, minHeight: '130px' }}>
        <div style={{ position: 'absolute', left: '2rem', top: '10px', display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer', transition: 'transform 0.2s' }}
        onClick={() => setShowAuth(false)}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}>
          <img src="/campus2career_logo.png" alt="Campus2Career Logo" style={{ height: '100px', width: 'auto', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
          <div style={{ color: 'white', marginLeft: '15px', fontSize: '0.85rem', fontWeight: '500', opacity: 0.9, letterSpacing: '0.3px', lineHeight: '1.2', textAlign: 'left' }}>
            <div>Smarten Your Search.</div>
            <div>Sharpen Your CV.</div>
          </div>
        </div>
        
        <span style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.2)', letterSpacing: '0.5px', cursor: 'pointer', transition: 'transform 0.2s' }}
        onClick={() => setShowAuth(false)}
        onMouseEnter={(e) => { e.target.style.transform = 'scale(1.05)'; }}
        onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}>
          Campus2Career
        </span>
      </div>

      {/* Title section */}
      <div style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(20px)', padding: '1.5rem 1rem', textAlign: 'center', width: '100%', position: 'relative', overflow: 'hidden', zIndex: 1 }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: 'white', textShadow: '0 2px 10px rgba(0,0,0,0.2)', position: 'relative', zIndex: 1 }}>
          {initialAuthMode === 'signin' ? 'Welcome Back!' : 'Join Campus2Career'}
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', marginTop: '0.3rem', position: 'relative', zIndex: 1 }}>
          {initialAuthMode === 'signin' ? 'Sign in to continue your career journey' : 'Create an account to kickstart your career'}
        </p>
      </div>

      {/* Alert notification */}
      {showAlert && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: alertType === 'success' ? '#10B981' : alertType === 'error' ? '#EF4444' : '#3B82F6',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          zIndex: 1001,
          maxWidth: '400px',
          fontSize: '0.95rem',
          fontWeight: '500'
        }}>
          {alertMessage}
        </div>
      )}

      {/* Main auth container */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center', 
        padding: '2rem 1rem', 
        paddingBottom: currentFormType === 'signup' ? '150px' : '2rem',
        position: 'relative', 
        zIndex: 1 
      }}>
        <div className="auth-content" style={{ maxWidth: '500px', width: '100%', position: 'relative' }}>
          <div style={{ position: 'absolute', top: '-2px', left: '-2px', right: '-2px', bottom: '-2px', background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)', borderRadius: '18px', zIndex: -1 }} />
          
          <div style={{ background: 'rgba(255,255,255,0.95)', borderRadius: '16px', overflow: 'hidden', backdropFilter: 'blur(20px)', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', padding: '2rem 3rem 2rem 2rem', border: '3px solid rgba(255,255,255,0.3)', position: 'relative', minHeight: initialAuthMode === 'signup' ? '550px' : '500px' }}>
            
            {/* Loading overlay */}
            {isLoading && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(10px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: '16px' }}>
                <div style={{ width: '50px', height: '50px', border: '4px solid rgba(102, 126, 234, 0.2)', borderTop: '4px solid #667eea', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '1.5rem', fontSize: '1.1rem', fontWeight: '600', color: '#667eea', textAlign: 'center' }}>{loadingMessage}</p>
                <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'rgba(102, 126, 234, 0.7)', textAlign: 'center' }}>Please wait...</p>
              </div>
            )}
            
            <div style={{ marginLeft: '-1.25rem', width: 'calc(100% + 3rem)', opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}>
              <Authenticator
                initialState={initialAuthMode === 'signup' ? 'signUp' : 'signIn'}
                components={components}
                hideSignUp={initialAuthMode === 'signin'}
                socialProviders={[]}
                formFields={{
                  signUp: {
                    given_name: { label: 'First Name', placeholder: 'Enter your first name', isRequired: true },
                    family_name: { label: 'Last Name', placeholder: 'Enter your last name', isRequired: true },
                    phone_number: { isRequired: false, display: 'none' },
                  },
                  confirmSignUp: {
                    confirmation_code: { label: 'Email Verification Code', placeholder: 'Enter the code sent to your email' }
                  }
                }}
              >
                {({ user }) => {
                  if (user) {
                    // Clear any previous user's data and set new user
                    clearUserData();
                    setUser(user);
                    setShowAuth(false);
                  }
                  return null;
                }}
              </Authenticator>
            </div>
          </div>
          
          {/* Cancel button */}
          <button onClick={() => setShowAuth(false)}
            style={{ display: 'block', width: '100%', marginTop: '1.5rem', padding: '0.75rem 2rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '25px', fontWeight: '600', fontSize: '1rem', cursor: 'pointer', textAlign: 'center', transition: 'all 0.3s', position: 'relative', overflow: 'hidden', boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)' }}
            onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)'; }}
            onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)'; }}>
            Cancel
          </button>
          
          {/* Help text */}
          <p style={{ textAlign: 'center', marginTop: '2rem', color: 'white', fontSize: '0.9rem', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(20px)', padding: '1rem', borderRadius: '8px' }}>
            {initialAuthMode === 'signin' ? "Don't have an account? Click Cancel and select Create Account" : "Already have an account? Click Cancel and select Sign In"}
          </p>
        </div>
      </div>

      {/* Email verification modal */}
      {showUnconfirmedModal && <EmailVerificationModal />}

      {/* Footer */}
      <div style={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#2e3a46', 
        padding: '2rem', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: '1rem', 
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)', 
        zIndex: 1 
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ color: 'white', fontSize: '1rem', fontWeight: '500', letterSpacing: '0.5px' }}>2025 Campus2Career</span>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '1rem', margin: '0 0.5rem' }}>•</span>
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>Powered by <strong style={{ color: '#ffffff', fontWeight: '600' }}>OpenAI GPT-4o</strong></span>
        </div>
        <div style={{ width: '300px', height: '3px', background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)', borderRadius: '2px', opacity: 0.8 }} />
      </div>
    </div>
  );
};

export default Login;